import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getRoot } from 'mobx-easy';
import { TaskStatus, TenderNotifyStatus } from '@bringg/types';
import { Notification } from '@bringg/react-components';
import i18next from 'i18next';
import { DynamicFormStringType, FORM_CONTROLS, FormFields } from '@bringg-frontend/dynamic-form-modal';

import RootStore from 'bringg-web/stores/root-store';

export const TENDER_NOTIFY_UUID = '51c2f221-f740-4119-85c9-06ab6341263c';

const NOTE_POSTFIX = `-------------------\ninternal-uuid: ${TENDER_NOTIFY_UUID}`;

export function isEnabled(): boolean {
	return !!getRoot<RootStore>().data?.merchantConfigurationsStore?.configuration?.enable_tender_notify;
}

function getSdkStore() {
	return getRootEnv().dashboardSdk.sdk.v2.taskManagedAttributes;
}

export async function loadForTask(taskId: number) {
	if (!isEnabled()) {
		return;
	}

	await getSdkStore().loadByIds([taskId], { useCache: true });
}

export function get(taskId: number): TenderNotifyStatus | undefined {
	if (!isEnabled()) {
		return undefined;
	}
	const task = getSdkStore().get(taskId);

	return task?.TenderNotifyStatus;
}

export function canShowForm(taskId: number): boolean {
	if (!isEnabled()) {
		return false;
	}

	if (getWayPointForTask(taskId) == undefined) {
		return false;
	}

	return canChangeForTask(taskId);
}

export function canChangeForTask(taskId: number): boolean {
	if (!isEnabled()) {
		return false;
	}

	const task = getRoot<RootStore>().data.tasksStore.get(taskId);
	if (!task) {
		return undefined;
	}

	const status = get(taskId);

	return (
		status != undefined &&
		status !== TenderNotifyStatus.Appointed &&
		![TaskStatus.Done, TaskStatus.Cancelled].includes(task?.status)
	);
}

function getWayPointForTask(taskId: number): Bringg.WayPoint | undefined {
	const task = getRoot<RootStore>().data.tasksStore.get(taskId);
	if (!task) {
		return undefined;
	}

	if (!task.way_points?.length) {
		return undefined;
	}

	return task.way_points[task.way_points.length - 1];
}

export async function submitTenderNotify(taskId: number, values: Record<string, any>) {
	if (!isEnabled()) {
		return;
	}

	const { tasksStore } = getRoot<RootStore>().data;
	if (!canShowForm(taskId)) {
		console.warn('cannot show form for task', taskId);
		Notification.error(i18next.t('TENDER_NOTIFY.FORM.ERRORS.GENERAL_FAILURE'));
		return;
	}

	const translations = getFormTranslations();

	let error;

	try {
		const result = await tasksStore.addTaskNote({
			taskId,
			wayPointId: getWayPointForTask(taskId)?.id,
			note: [
				`${translations.modalTitle}:`,
				`${translations.contactName}: ${values.contact_name}`,
				`${translations.phone}: ${values.phone}`,
				`${translations.note}: ${values.note}`,
				NOTE_POSTFIX
			].join('\n'),
			hiddenFromDriver: true
		});

		if (!result) {
			error = new Error('failed to create note');
		}
	} catch (e) {
		error = e;
	}

	if (error) {
		console.error('Failed to created tender notify note', taskId);
		Notification.error(i18next.t('TENDER_NOTIFY.FORM.ERRORS.FAILED_TO_CREATE_NOTE'));
	}
}

export function getForm(taskId: number): FormFields[] {
	// Form should be updated in case task or wp is changed but not while displaying
	const wp = getWayPointForTask(taskId);

	const translations = getFormTranslations();

	return [
		{
			key: 'contact_name',
			label: translations.contactName,
			control: FORM_CONTROLS.INPUT,
			type: DynamicFormStringType.string,
			mandatory: true,
			initialValue: wp?.customer?.name
		},
		{
			key: 'phone',
			label: translations.phone,
			control: FORM_CONTROLS.INPUT,
			type: DynamicFormStringType.string,
			mandatory: false,
			initialValue: wp?.customer?.phone
		},
		{
			key: 'note',
			label: translations.note,
			control: FORM_CONTROLS.TEXTBOX,
			mandatory: true
		}
	];
}

function getFormTranslations() {
	return {
		modalTitle: i18next.t('TENDER_NOTIFY.FORM.TITLE'),
		contactName: i18next.t('TENDER_NOTIFY.FORM.CONTACT_NAME'),
		phone: i18next.t('TENDER_NOTIFY.FORM.PHONE'),
		note: i18next.t('TENDER_NOTIFY.FORM.NOTE')
	};
}
